import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, Camera, Check, Download, Lock, LockOpen, Pencil, Plus, ScanLine, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarcodeScanner } from "@/components/colis/BarcodeScanner";
import { ChampLecteur } from "@/components/colis/ChampLecteur";
import { EcheanceBadge } from "@/components/colis/Echeance";
import { fmtDateHeure } from "@/components/scans/common";
import { useAuth } from "@/context/AuthContext";
import { API, api, errMsg } from "@/lib/api";
import { bip } from "@/lib/bip";
import { AIDE_AWB, lireCode, numeroLisible } from "@/lib/colisCode";
import { colisSubnav } from "@/lib/subnavs";
import { StatutColis } from "./ColisPage";
import { StatutListe } from "./ListesColisPage";
import { peut } from "@/lib/permissions";
import { InfoFichier } from "@/components/depot/InfoFichier";
import { useDouchette } from "@/lib/douchette";

/**
 * Une liste de colis : scan en continu (caméra) ET saisie au clavier (ou
 * douchette) en même temps ; chaque airwaybill s'ajoute à la liste.
 * Consultation, export CSV, clôture.
 */
export default function ListeColisPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [l, setL] = useState(null);
  const [camera, setCamera] = useState(params.get("scan") === "1");
  const [saisie, setSaisie] = useState("");
  const [retour, setRetour] = useState(null); // dernier ajout (affiché sur la caméra et sous le champ)
  const [renommer, setRenommer] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const enCours = useRef(new Set()); // n° en cours d'envoi (évite les doubles envois)
  const champ = useRef(null);

  const charger = useCallback(async () => {
    try { const { data } = await api.get(`/listes-colis/${id}`); setL(data); }
    catch (e) { toast.error(errMsg(e)); }
  }, [id]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { if (params.get("scan")) { params.delete("scan"); setParams(params, { replace: true }); } }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ecrire = peut(user, "listes_creer");
  const ouverte = l?.statut !== "cloturee" && ecrire;   // ajout de colis : liste ouverte + permission

  const ajouter = useCallback(async (brut, source) => {
    const lu = lireCode(brut);
    if (!lu.type) {
      bip(false);
      setRetour({ texte: `Refusé : ce n'est pas un airwaybill`, ok: false, t: Date.now() });
      return false;
    }
    if (enCours.current.has(lu.numero)) return false;
    enCours.current.add(lu.numero);
    try {
      const { data } = await api.post(`/listes-colis/${id}/items`, { numero: brut, source });
      bip(true);
      setRetour({ texte: `✓ ${numeroLisible(data.item.numero)} ajouté (${data.nb})`, ok: true, t: Date.now() });
      setL((x) => (x ? { ...x, nb: data.nb, items: [data.item, ...x.items] } : x));
      return true;
    } catch (e) {
      bip(false);
      const dup = e?.response?.status === 409;
      setRetour({ texte: dup ? `Déjà dans la liste : ${numeroLisible(lu.numero)}` : errMsg(e), ok: false, t: Date.now() });
      return dup; // doublon : on vide quand même le champ
    } finally {
      enCours.current.delete(lu.numero);
    }
  }, [id]);

  // Lecteur intégré (Zebra) / douchette : le colis est ajouté sans cliquer dans le champ.
  useDouchette((code) => { setSaisie(""); ajouter(code, "scan"); }, !!l && ouverte);

  const valider = async () => {
    if (!saisie.trim()) return;
    if (await ajouter(saisie, "saisie")) setSaisie("");
    champ.current?.focus();
  };

  const patch = async (body, msg) => {
    try { const { data } = await api.patch(`/listes-colis/${id}`, body); setL(data); if (msg) toast.success(msg); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };
  const retirer = async (it) => {
    try { await api.delete(`/listes-colis/${id}/items/${it.id}`); setL((x) => ({ ...x, nb: x.nb - 1, items: x.items.filter((i) => i.id !== it.id) })); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const supprimer = async () => {
    try { await api.delete(`/listes-colis/${id}`); toast.success("Liste supprimée"); navigate("/colis/listes"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const peutSupprimer = user?.is_admin || (peut(user, "listes_supprimer") && l?.created_by?.matricule === user?.matricule);
  const apercu = saisie.trim() ? lireCode(saisie) : null;

  return (
    <AppShell title="Liste de colis" subnav={colisSubnav()}>
      <Link to="/colis/listes" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={15} /> Toutes les listes</Link>
      {!l ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div className="min-w-0">
              {renommer !== null ? (
                <form className="flex gap-2" onSubmit={async (e) => { e.preventDefault(); if (await patch({ nom: renommer })) setRenommer(null); }}>
                  <Input value={renommer} onChange={(e) => setRenommer(e.target.value)} maxLength={120} autoFocus className="h-10 text-lg font-semibold" data-testid="liste-renommer" />
                  <Button type="submit" size="icon" aria-label="Valider"><Check size={16} /></Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => setRenommer(null)} aria-label="Annuler"><X size={16} /></Button>
                </form>
              ) : (
                <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="liste-titre">
                  {l.nom}
                  {ecrire && <button onClick={() => setRenommer(l.nom)} className="p-1 rounded text-muted-foreground hover:text-foreground" aria-label="Renommer"><Pencil size={15} /></button>}
                </h1>
              )}
              <p className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
                <StatutListe l={l} />
                <span data-testid="liste-nb"><b className="text-foreground">{l.nb}</b> colis</span>
                {Object.entries(l.par_type).map(([k, v]) => <span key={k}>· {k} : {v}</span>)}
                <span>· créée le {fmtDateHeure(l.created_at)} par {l.created_by?.nom || "—"}</span>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {peut(user, "listes_export") && <a href={`${API}/listes-colis/${id}/export.csv`} download className={`inline-flex items-center gap-2 h-9 px-4 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 ${l.nb ? "" : "pointer-events-none opacity-50"}`} data-testid="liste-export">
                <Download size={15} /> Exporter CSV
              </a>}
              {!ecrire ? null : l.statut !== "cloturee"
                ? <Button variant="outline" onClick={() => { setCamera(false); patch({ statut: "cloturee" }, "Liste clôturée"); }} data-testid="liste-cloturer"><Lock size={15} /> Clôturer</Button>
                : <Button variant="outline" onClick={() => patch({ statut: "ouverte" }, "Liste rouverte")} data-testid="liste-rouvrir"><LockOpen size={15} /> Rouvrir</Button>}
              {peutSupprimer && (confirmDel
                ? <Button variant="destructive" onClick={supprimer}>Supprimer la liste ?</Button>
                : <Button variant="ghost" className="text-destructive" onClick={() => setConfirmDel(true)} aria-label="Supprimer la liste"><Trash2 size={15} /></Button>)}
            </div>
          </div>

          {ouverte && (
            <section className="grid gap-4 lg:grid-cols-2 mb-6" data-testid="liste-ajout">
              <div className="rounded-xl border border-border bg-card p-4">
                <h2 className="font-heading font-semibold mb-3 flex items-center gap-2"><ScanLine size={16} /> Scanner ou saisir</h2>
                <ChampLecteur ref={champ} value={saisie} onChange={setSaisie} onSubmit={valider} testId="liste-saisie" boutonLabel="Ajouter"
                  placeholder="N° de colis…" actif={ouverte && !confirmDel} />
                <p className="text-xs text-muted-foreground mt-2">
                  {apercu?.type ? <>Sera ajouté : <span className="font-mono text-foreground">{apercu.numero}</span></>
                    : apercu ? <span className="text-red-600 dark:text-red-400">Pas un airwaybill : {AIDE_AWB}.</span>
                      : "Terminal Zebra ou douchette : scannez directement, chaque colis est ajouté."}
                </p>
                {retour && (
                  <p className={`mt-3 text-sm font-semibold ${retour.ok ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`} data-testid="liste-retour">{retour.texte}</p>
                )}
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h2 className="font-heading font-semibold flex items-center gap-2"><Camera size={16} /> Caméra</h2>
                  {!camera && <Button size="sm" variant="outline" onClick={() => setCamera(true)} data-testid="liste-camera"><Camera size={14} /> Activer le scan caméra</Button>}
                </div>
                {camera
                  ? <BarcodeScanner continu retour={retour} onDetected={(r) => ajouter(r.text, "scan")} onCancel={() => setCamera(false)} />
                  : <p className="text-sm text-muted-foreground">Inutile avec un terminal Zebra ou une douchette. La caméra reste ouverte et ajoute chaque airwaybill lu (bip + vibration).</p>}
              </div>
            </section>
          )}

          <div className="rounded-xl border border-border bg-card overflow-x-auto">
            <Table data-testid="liste-items">
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead><TableHead>N° de colis</TableHead><TableHead>Expéditeur / destinataire</TableHead><TableHead>Type</TableHead><TableHead>Fiche colis</TableHead>
                  <TableHead>Ajouté le</TableHead><TableHead>Par</TableHead><TableHead>Saisie</TableHead><TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {l.items.map((it, i) => (
                  <TableRow key={it.id} data-testid={`item-${it.numero}`}>
                    <TableCell className="text-xs text-muted-foreground">{l.items.length - i}</TableCell>
                    <TableCell className="font-mono font-bold whitespace-nowrap">
                      {it.fiche && peut(user, "colis_colis") ? <Link to={`/colis/${it.fiche.id}`} className="hover:underline">{numeroLisible(it.numero)}</Link> : numeroLisible(it.numero)}
                      {it.depot && <span className="block font-sans font-normal text-[11px] text-muted-foreground">Dépôt : {it.depot.emplacement}</span>}
                    </TableCell>
                    <TableCell className="min-w-[200px]">{it.info ? <InfoFichier info={it.info} compact numero={it.numero} /> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{it.type_label}</TableCell>
                    <TableCell>
                      {it.fiche ? (
                        <span className="flex flex-wrap items-center gap-1">
                          <StatutColis c={it.fiche} detail />
                          {it.fiche.echeance && <EcheanceBadge e={it.fiche.echeance} court />}
                        </span>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{fmtDateHeure(it.ajoute_le)}</TableCell>
                    <TableCell className="text-sm">{it.par?.nom || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{it.source === "scan" ? "Scan" : "Saisie"}</TableCell>
                    <TableCell className="text-right">
                      {ouverte && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => retirer(it)} aria-label="Retirer de la liste"><X size={14} /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {!l.items.length && <TableRow><TableCell colSpan={9} className="text-sm text-muted-foreground">Liste vide : scannez ou saisissez un premier colis.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
          {l.nb > 0 && (
            <p className="text-xs text-muted-foreground mt-2">
              CSV au format Excel (séparateur « ; »). Pour un autre logiciel : <a className="underline" href={`${API}/listes-colis/${id}/export.csv?brut=1`} download>CSV brut</a>.
            </p>
          )}
        </>
      )}
    </AppShell>
  );
}
